import { useEffect, useState, useContext } from "react";
import { MagnifyingGlassIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import PageHeader from "../../components/page-header";
import Table from "../../components/table";
import { sub, add, compareDesc, isAfter } from "date-fns";
import { useTextFormatHook } from "../../hooks/text-formatter";
import { useDataLoaderHook } from "../../hooks/data-loader";
import { ApiConsumer } from "../../api/ApiConsumer";
import { NavLink } from "react-router-dom";
import { GLOBAL } from "../../constants/global";
import { API_ROUTES } from "../../routes/api";
import { PORTAL } from "../../routes/portal";
import * as Sentry from "@sentry/react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AppLoader } from "../../components/loader/app-loader";
import { AppLoaderContext } from "../../contexts/app-loader";
import AddEditRisk from "../../components/forms/add-edit-risk";
import Modal from "../../components/modal";

export default function AuditingActions() {
    let { setLoading } = useContext(AppLoaderContext);
    let { normaliseTableData } = useTextFormatHook();
    let { loadMultiple } = useDataLoaderHook();
    const [formDetails, setFormDetails] = useState({});
    const [kpas, setKpas] = useState([]);
    const [projects, setProjects] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [projectsReport, setProjectsReport] = useState([]);
    const [openAddEditRisk, setCloseAddEditRisk] = useState(false);
    const [editableRow, setEditableRow] = useState({});
    const [rowUpdated, setRowUpdated] = useState(false);

    const addProjectLinksToTableData = (tableData) => {
        tableData.forEach((row) => {
            console.log(row)
            row.normalised.description = (
                <NavLink
                    to={`${PORTAL.TASKS_BY_PROJECT}/${row.id}`}
                    className={`brand-link`}
                >
                    {row.normalised.description}
                </NavLink>
            );
        });
        return tableData;
    };

    const searchReport = () => {
        setLoading(true);
        for (const key in formDetails) {
            if (
                Array.isArray(formDetails[key]) &&
                formDetails[key].length === 0
            ) {
                delete formDetails[key];
            }
        }

        ApiConsumer.get(API_ROUTES.AUDIT.ACTIONS, formDetails)
            .then((res) => {
                res.data.sort((a, b) =>
                    compareDesc(new Date(a.updated), new Date(b.updated))
                );
                let normalisedData = normaliseTableData(
                    GLOBAL.AUDIT_ACTIONS_HEADERS,
                    res.data
                );
                console.log("NORMALISED")
                console.log(normalisedData)
                setProjectsReport(normalisedData);
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onProjectDateChange = (dates) => {
        const [start, end] = dates;

        setFormDetails((prevState) => ({
            ...prevState,
            [`project_start_date`]: start,
        }));
        setFormDetails((prevState) => ({
            ...prevState,
            [`project_end_date`]: end,
        }));
    };

    const loadData = () => {
        setLoading(true);
        ApiConsumer.get(API_ROUTES.AUDIT.ACTIONS)
            .then((res) => {
                res.data.sort((a, b) =>
                    compareDesc(new Date(a.created), new Date(b.created))
                );

                let normalisedData = normaliseTableData(
                    GLOBAL.AUDIT_ACTIONS_HEADERS,
                    res.data
                );
                console.log("NORMALISED")
                console.log(normalisedData)
                setProjectsReport(addProjectLinksToTableData(normalisedData));
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    };



    useEffect(() => {
        setFormDetails((prevState) => ({
            ...prevState,
            [`project_start_date`]: sub(new Date(), { years: 1 }),
        }));
        setFormDetails((prevState) => ({
            ...prevState,
            [`project_end_date`]: add(new Date(), { years: 1 }),
        }));
    }, []);

    useEffect(() => {
        loadData()
        loadMultiple(API_ROUTES.CONTENT.KPI, setKpas);
    }, []);

    const clearSearch = () => {
        setFormDetails((prevState) => ({
            ...prevState,
            [`kpas`]: [],
        }));
    };

    return (
        <>
            <PageHeader pageHeaderName={`Audit Actions (${projectsReport.length})`} />

            <div className="mx-full px-4 sm:px-6 lg:px-8 pt-2 my-2">
                <div className="grid grid-cols-12 space-x-4">
                    <div className="col-span-4">
                        <label className="text-sm" htmlFor="kpas">
                            KPIs:
                        </label>
                        <Select
                            id="kpas"
                            name="kpas"
                            placeholder="All kpis"
                            isMulti
                            options={kpas}
                            classNamePrefix="multi-select"
                            className={`multi-select text-xs`}
                            onChange={(event) => {
                                setFormDetails((prevState) => ({
                                    ...prevState,
                                    [`kpas`]: [...event].map(
                                        (option) => option.value
                                    ),
                                }));
                            }}
                            defaultValue={0}
                            value={
                                Object.keys(kpas).length !== 0 &&
                                formDetails.kpas !== undefined
                                    ? formDetails?.kpas?.map(
                                        (selectedOption) => {
                                            return kpas[
                                                kpas.findIndex(
                                                    (option) =>
                                                        option.value ===
                                                        selectedOption
                                                )
                                                ];
                                        }
                                    )
                                    : 0
                            }
                        />
                    </div>
                </div>
            </div>

            <div className="relative py-2">
                <AppLoader
                    pageContent={
                        <h2 className="flex mx-full px-4 sm:px-6 lg:px-8 text-lg leading-6 text-gray-900 text-left items-center">
                            <span className="min-w-0 flex-1 items-center font-medium">
                                Audit Actions Results ({projectsReport.length})
                                <br/>
                                <span
                                    className="brand-link text-sm font-normal"
                                    onClick={() => clearSearch()}
                                >
                                    Clear all search parameters
                                </span>
                            </span>

                            <div className="col-span-3">
                                <label className="text-sm" htmlFor="date_range">
                                    Date range:
                                </label>
                                <DatePicker
                                    onKeyDown={(e) => {
                                        e.preventDefault();
                                    }}
                                    showYearDropdown
                                    dateFormat="dd MMMM yyyy"
                                    selected={formDetails.project_start_date}
                                    onChange={(event) =>
                                        onProjectDateChange(event)
                                    }
                                    className="form-field"
                                    startDate={formDetails.project_start_date}
                                    endDate={formDetails.project_end_date}
                                    selectsRange
                                />
                            </div>
                            <button
                                type="button"
                                className="button flex-shrink-0 ml-2 mt-5"
                                onClick={() => searchReport()}
                            >
                                <div
                                    className="flex items-center pointer-events-none mr-1"
                                    aria-hidden="true"
                                >
                                    <MagnifyingGlassIcon
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                    />
                                </div>
                                Search
                            </button>
                        </h2>
                    }
                />
            </div>
            <Table
                tableTitle={`Audit Action Results`}
                tableHeaders={GLOBAL.AUDIT_ACTIONS_HEADERS}
                tableData={projectsReport}
                fontSize="text-xs"
            />
        </>
    );
}
